import { Tab, Tabs } from "@mui/material";
import { useState } from "react";
import {
  assessmentScorePillars,
  getAssessmentCategoryScore,
} from "../../../../form/components/integrity-scores/scoreCategories";
import { getScoreDisplayValue } from "../../../../formatting/domain-value-manipulation";
import { isMissingDisplayValue } from "../../../../formatting/string-manipulation";
import { getPillarScores } from "../../../fields/scores";
import type { DetailedSolutionApiResponse } from "../../../../types";
import { buildBenefitsFields } from "../../../fields/benefits";
import { buildScoresFields } from "../../../fields/scores";
import Section from "../../common/Section/Section";
import type { AssessmentSectionComponentProps, Field } from "../../types";

function buildDataFields(details: DetailedSolutionApiResponse): Array<Field> {
  const { pillars, SDG } = details;

  const scoreFields = buildScoresFields(pillars);
  const totalScoreField = { ...scoreFields[3], fullLine: true };
  const benefitFields = buildBenefitsFields(pillars, 4);
  const [
    carbonQuantity,
    carbonDescription,
    biodiversityQuantity,
    biodiversityDescription,
    peopleQuantity,
    peopleDescription,
  ] = benefitFields;

  return [
    scoreFields[0],
    scoreFields[1],
    scoreFields[2],
    totalScoreField,
    { ...carbonQuantity, index: 4 },
    { ...biodiversityQuantity, index: 5 },
    { ...peopleQuantity, index: 6 },
    { ...carbonDescription, index: 7 },
    { ...biodiversityDescription, index: 8 },
    { ...peopleDescription, index: 9 },
    {
      name: "SDGs",
      type: "sdg",
      index: 10,
      fullLine: true,
      value: SDG ?? [],
    },
  ];
}

function DataSection({ details, onEdit }: AssessmentSectionComponentProps) {
  const { pillars, SDG } = details;
  const [selectedTab, setSelectedTab] = useState("overview");
  const pillar = assessmentScorePillars.find(({ key }) => key === selectedTab);
  const integrityFields: Array<Field> = pillar
    ? pillar.categories.flatMap(({ key, label }, index) => {
        const score = getAssessmentCategoryScore(
          details.pillars,
          pillar.key,
          key,
        );
        return [
          {
            name: `${label} score`,
            type: "text",
            index: index * 2,
            value: getScoreDisplayValue(score?.score),
          },
          {
            name: `${label} explanation`,
            type: "text",
            index: index * 2 + 1,
            value: score?.explanation,
            columnSpan: 3,
          },
        ];
      })
    : [];
  const benefitsFields: Array<Field> = (
    details.assessmentReport?.benefits?.length
      ? details.assessmentReport.benefits
      : [""]
  ).map((value, index) => ({
    name: `Unique benefit ${index + 1}`,
    type: "text",
    index,
    value,
    fullLine: true,
  }));
  const missingOverview =
    !SDG?.length ||
    getPillarScores(pillars).some(isMissingDisplayValue) ||
    ["carbon", "biodiversity", "people"].some((key) => {
      const benefit =
        pillars?.[key as "carbon" | "biodiversity" | "people"]?.benefits;
      return (
        isMissingDisplayValue(benefit?.quantity) ||
        !benefit?.description?.trim()
      );
    });
  const missingIntegrity = (
    pillarKey: (typeof assessmentScorePillars)[number]["key"],
  ): boolean => {
    const selectedPillar = assessmentScorePillars.find(
      ({ key }) => key === pillarKey,
    );
    if (!selectedPillar) return false;
    return selectedPillar.categories.some(({ key }) => {
      const category = getAssessmentCategoryScore(
        details.pillars,
        selectedPillar.key,
        key,
      );
      return (
        category?.score === undefined ||
        category?.score === null ||
        !category?.explanation?.trim()
      );
    });
  };
  const missingBenefits =
    !details.assessmentReport?.benefits?.length ||
    details.assessmentReport.benefits.some((benefit) => !benefit.trim());
  const tabs = [
    { key: "overview", label: "Overview", missing: missingOverview },
    {
      key: "carbon",
      label: "Carbon integrity",
      missing: missingIntegrity("carbon"),
    },
    {
      key: "biodiversity",
      label: "Biodiversity integrity",
      missing: missingIntegrity("biodiversity"),
    },
    {
      key: "people",
      label: "People integrity",
      missing: missingIntegrity("people"),
    },
    { key: "benefits", label: "Unique benefits", missing: missingBenefits },
  ];

  return (
    <Section
      title="Data"
      columns={
        selectedTab === "overview" ? 3 : selectedTab === "benefits" ? 1 : 4
      }
      fields={
        selectedTab === "overview"
          ? buildDataFields(details)
          : selectedTab === "benefits"
            ? benefitsFields
            : integrityFields
      }
      onEdit={() => onEdit("data")}

      aboveFields={
        <Tabs
          value={selectedTab}
          onChange={(_event, value: string) => setSelectedTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="Assessment report data tabs"
          sx={{ mb: 3 }}
        >
          {tabs.map(({ key, label, missing }) => (
            <Tab
              key={key}
              value={key}
              label={label}
              sx={{ color: missing ? "error.main" : undefined }}
            />
          ))}
        </Tabs>
      }
    />
  );
}

export default DataSection;
